/**
 * Australian date and time formatting.
 * All storage is UTC; display in the user's configured timezone (default: Australia/Brisbane).
 */

const DEFAULT_TZ = "Australia/Brisbane"

// Deterministic short month names so "15 Jul 2025" renders identically on
// every Node ICU version. Intl "short" varies between platforms ("July" vs
// "Jul"), which showed up in the Cloudflare Worker vs local Node divergence.
const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const

/**
 * AU short date: "15 Jan 2027". Uses hand-rolled month names so it renders
 * identically across environments (Node ICU, V8 in Workers, Firefox etc.).
 */
export function formatDate(input: Date | string | number | null, tz = DEFAULT_TZ): string {
  if (input === null || input === undefined) return "—"
  const date = toDate(input)
  if (!date) return "—"
  // Extract day / month / year in the target timezone.
  const parts = new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: tz,
  }).formatToParts(date)
  const day = parts.find((p) => p.type === "day")?.value ?? ""
  const monthNum = Number(parts.find((p) => p.type === "month")?.value ?? "0")
  const year = parts.find((p) => p.type === "year")?.value ?? ""
  const month = SHORT_MONTHS[monthNum - 1] ?? ""
  return `${day} ${month} ${year}`
}

export function formatDateLong(input: Date | string | number | null, tz = DEFAULT_TZ): string {
  if (input === null || input === undefined) return "—"
  const date = toDate(input)
  if (!date) return "—"
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: tz,
  }).format(date)
}

/**
 * AU date + time: "15 Jan 2027, 2:30 pm". Consistent short-month rendering
 * across environments.
 */
export function formatDateTime(input: Date | string | number | null, tz = DEFAULT_TZ): string {
  if (input === null || input === undefined) return "—"
  const date = toDate(input)
  if (!date) return "—"
  const datePart = formatDate(date, tz)
  const timePart = new Intl.DateTimeFormat("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(date)
  return `${datePart}, ${timePart}`
}

export function formatTime(input: Date | string | number | null, tz = DEFAULT_TZ): string {
  if (input === null || input === undefined) return "—"
  const date = toDate(input)
  if (!date) return "—"
  return new Intl.DateTimeFormat("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  }).format(date)
}

/**
 * "Today" as a d/m/yyyy string in Brisbane. Used by the {{today}} merge
 * field and any "today" preview.
 */
export function todayInBrisbane(now: Date = new Date(), tz = DEFAULT_TZ): string {
  const parts = new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: tz,
  }).formatToParts(now)
  const day = parts.find((p) => p.type === "day")?.value ?? ""
  const month = parts.find((p) => p.type === "month")?.value ?? ""
  const year = parts.find((p) => p.type === "year")?.value ?? ""
  return `${day}/${month}/${year}`
}

/**
 * Return Brisbane calendar components (year, month, day, hour, minute)
 * for a given instant. Useful for grouping by "same day in Brisbane"
 * regardless of the runtime's local timezone.
 */
export function brisbaneParts(input: Date | string | number, tz = DEFAULT_TZ): {
  year: number
  month: number
  day: number
  hour: number
  minute: number
} | null {
  const date = toDate(input)
  if (!date) return null
  const parts = new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
  }).formatToParts(date)
  const num = (t: string) =>
    Number(parts.find((p) => p.type === t)?.value ?? "0")
  const rawHour = num("hour")
  return {
    year: num("year"),
    month: num("month"),
    day: num("day"),
    // Intl sometimes yields 24 for midnight; normalise to 0.
    hour: rawHour === 24 ? 0 : rawHour,
    minute: num("minute"),
  }
}

/**
 * Relative time: "just now", "5m ago", "2h ago", "3d ago", or a formatted date.
 *
 * NOTE: this function is for PAST timestamps. Passing a future timestamp will
 * return "just now" (because the diff is negative and the < 60 branch wins).
 * Use `timeUntil()` for future timestamps.
 */
export function timeAgo(input: Date | string | number | null, tz = DEFAULT_TZ): string {
  if (input === null || input === undefined) return "—"
  const date = toDate(input)
  if (!date) return "—"

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 4) return `${weeks}w ago`
  return formatDate(date, tz)
}

/**
 * Relative time for FUTURE timestamps: "in a few sec", "in 5m", "in 2h",
 * "in 3 days", "in 2 weeks", "in 4 months", "in 2 years", or a formatted date
 * for anything past ~2 years out.
 *
 * Returns "—" for null / invalid inputs, and "just now" if the timestamp is
 * within 30 seconds (past or future) so we don't flicker for near-now.
 *
 * Anchors on Brisbane calendar days for anything a day or more out, so
 * "in 3 days" means 3 calendar days regardless of what hour the run
 * lands at.
 */
export function timeUntil(
  input: Date | string | number | null,
  tz = DEFAULT_TZ
): string {
  if (input === null || input === undefined) return "—"
  const date = toDate(input)
  if (!date) return "—"

  const nowMs = Date.now()
  const diffMs = date.getTime() - nowMs
  const absSec = Math.abs(diffMs) / 1000
  if (absSec < 30) return "just now"

  // Past values that reach here shouldn't happen for our use-case (waiting
  // runs with a future nextRunAt), but guard anyway.
  if (diffMs < 0) return timeAgo(input, tz)

  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `in ${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `in ${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `in ${hours}h`

  // For 1+ day out, count calendar days in Brisbane so the label doesn't
  // flicker across midnight based on wall-clock hours.
  const todayParts = brisbaneParts(new Date(nowMs))
  const targetParts = brisbaneParts(date)
  if (!todayParts || !targetParts) {
    // brisbaneParts can only return null on invalid dates — already guarded.
    // Fall back to plain day math if it ever happens.
    const days = Math.floor(diffMs / 86_400_000)
    return `in ${days} days`
  }
  const daysBetween = calendarDayDiff(todayParts, targetParts)

  if (daysBetween === 1) return "tomorrow"
  if (daysBetween < 7) return `in ${daysBetween} days`
  if (daysBetween < 14) return `in 1 week`
  if (daysBetween < 60) return `in ${Math.round(daysBetween / 7)} weeks`
  if (daysBetween < 365 * 2) {
    const months = Math.round(daysBetween / 30.4375)
    return months === 1 ? "in 1 month" : `in ${months} months`
  }
  const years = Math.floor(daysBetween / 365)
  return years === 1 ? "in 1 year" : `in ${years} years`
}

/**
 * Whole-day difference between two Brisbane calendar-day part objects.
 */
function calendarDayDiff(
  a: NonNullable<ReturnType<typeof brisbaneParts>>,
  b: NonNullable<ReturnType<typeof brisbaneParts>>
): number {
  const aMs = Date.UTC(a.year, a.month - 1, a.day)
  const bMs = Date.UTC(b.year, b.month - 1, b.day)
  return Math.round((bMs - aMs) / 86_400_000)
}

function toDate(input: Date | string | number): Date | null {
  if (input instanceof Date) return isValidDate(input) ? input : null
  if (typeof input === "number") {
    // Unix timestamp — support both seconds and milliseconds
    const ms = input < 10_000_000_000 ? input * 1000 : input
    const date = new Date(ms)
    return isValidDate(date) ? date : null
  }
  if (typeof input === "string") {
    const date = new Date(input)
    return isValidDate(date) ? date : null
  }
  return null
}

function isValidDate(date: Date): boolean {
  return !Number.isNaN(date.getTime())
}
